// Centralized API client utility
// Handles all API requests with proper headers and error handling

import { getApiUrl, API_CONFIG } from '../config/apiConfig';

/**
 * Makes an API request with proper headers and error handling
 * @param {string} endpoint - API endpoint from API_CONFIG.ENDPOINTS
 * @param {Object} options - Fetch options (method, body, headers, etc.)
 * @param {Object} params - Query parameters for GET requests
 * @returns {Promise<Response>} - Fetch response
 */
export const apiRequest = async (endpoint, options = {}, params = {}) => {
  const url = getApiUrl(endpoint, params);
  
  // Default headers
  const defaultHeaders = {
    'Accept': 'application/json',
  };

  // If body is FormData, don't set Content-Type (browser will set it with boundary)
  // Otherwise, set Content-Type to application/json
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Merge with custom headers
  const headers = {
    ...defaultHeaders,
    ...options.headers,
  };

  // Prepare request options
  const requestOptions = {
    ...options,
    headers,
    // Add credentials for cross-origin requests if needed
    credentials: 'include',
  };

  // If body is an object and not FormData, stringify it
  if (requestOptions.body && typeof requestOptions.body === 'object' && !(requestOptions.body instanceof FormData)) {
    requestOptions.body = JSON.stringify(requestOptions.body);
  }

  try {
    const response = await fetch(url, requestOptions);
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw new Error(`Network error: ${error.message}`);
  }
};

/**
 * Makes a GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} - Fetch response
 */
export const apiGet = async (endpoint, params = {}) => {
  return apiRequest(endpoint, { method: 'GET' }, params);
};

/**
 * Makes a POST request
 * @param {string} endpoint - API endpoint
 * @param {Object|FormData} body - Request body
 * @param {Object} headers - Additional headers
 * @returns {Promise<Response>} - Fetch response
 */
export const apiPost = async (endpoint, body = null, headers = {}) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body,
    headers,
  });
};

/**
 * Makes a PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object|FormData} body - Request body
 * @param {Object} headers - Additional headers
 * @returns {Promise<Response>} - Fetch response
 */
export const apiPut = async (endpoint, body = null, headers = {}) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body,
    headers,
  });
};

/**
 * Makes a DELETE request
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} - Fetch response
 */
export const apiDelete = async (endpoint, params = {}) => {
  return apiRequest(endpoint, { method: 'DELETE' }, params);
};

/**
 * Parses JSON response with error handling
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} - Parsed JSON data
 */
export const parseJsonResponse = async (response) => {
  const text = await response.text();
  
  // Check if response is HTML (error page)
  if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
    console.error('Received HTML instead of JSON. Response:', text.substring(0, 500));
    throw new Error('Server returned HTML page. The proxy may not be working. Please restart the dev server.');
  }
  
  // Try to parse as JSON
  try {
    return JSON.parse(text);
  } catch (parseError) {
    console.error('Failed to parse JSON. Response:', text.substring(0, 500));
    throw new Error('Invalid JSON response from server');
  }
};

